import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, Search, Ban, RotateCcw, Check, X } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { Skeleton } from '../components/ui/Skeleton';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { useAuthStore } from '../stores/authStore';
import { Role } from '../auth/roles';
import { formatEUR, formatDate } from '../lib/format';
import CustomerDocumentOverrides from '../components/CustomerDocumentOverrides';
import CustomerB2bUsers from '../components/CustomerB2bUsers';
import CustomerCreditReleases from '../components/CustomerCreditReleases';
import CustomerCollectors from '../components/CustomerCollectors';
import CustomerVatExemption, { type VatExemptionFields } from '../components/CustomerVatExemption';
import CustomerAccountCredit from '../components/CustomerAccountCredit';
import CustomerOnAccountMoney from '../components/CustomerOnAccountMoney';

interface Addr {
  line1?: string;
  line2?: string;
  postalCode?: string;
  city?: string;
  country?: string;
}
interface Relation extends VatExemptionFields {
  id: string;
  name: string;
  company_name?: string | null;
  siret?: string | null;
  vat_number?: string | null;
  customer_type: string;
  contact?: Record<string, unknown> | null;
  billing_address?: Addr | null;
  shipping_address?: Addr | null;
  credit_limit?: string | number | null;
  current_balance?: string | number;
  reserved_credit?: string | number;
  buyer_group_id?: string | null;
  payment_terms?: string | null;
  settlement_channel?: string | null;
  billing_mode?: string | null;
  client_code?: string | null;
  status?: string | null;
  version?: number;
  email_invoice?: string | null;
  email_packing_slip?: string | null;
  email_quotation?: string | null;
  email_order?: string | null;
  iban?: string | null;
  website?: string | null;
  billing_type?: string | null;
  credit_period?: number | null;
  agreement?: string | null;
  webshop_discount_percentage?: string | number | null;
  is_customer?: boolean | null;
  is_supplier?: boolean | null;
  block_sale?: boolean | null;
}

interface InvoiceRow {
  id: string;
  invoice_number: string;
  status: string;
  total_ttc: string | number;
  issued_at: string;
}
interface QuoteRow {
  id: string;
  quoteNumber?: string;
  status: string;
  totalTTC?: string | number;
  expiresAt?: string;
  customerId?: string;
}

// The sub-menu bar from the Relation Portal. `wired` tabs pull live data; the rest are
// placeholders for features without a backend feed yet (see relation form scope).
// Labels: relations.detail.tabs.<key>.
const TABS = [
  { key: 'data', wired: true },
  { key: 'invoices', wired: true },
  { key: 'unpaid', wired: true },
  { key: 'offers', wired: true },
  { key: 'documents', wired: true },
  { key: 'users', wired: true },
  // E8 — who may collect on this account. Read at the hand-over desk, kept here.
  { key: 'collectors', wired: true },
  { key: 'discounts', wired: false },
  { key: 'packing', wired: false },
  { key: 'delivery', wired: false },
  { key: 'products', wired: false },
  { key: 'mail', wired: false },
  { key: 'note', wired: false },
  { key: 'vehicles', wired: false },
  { key: 'pricing', wired: false },
] as const;

type TabKey = (typeof TABS)[number]['key'];

export default function RelationDetail() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { showApiError, push } = useToast();
  const [rel, setRel] = useState<Relation | null>(null);
  const [tab, setTab] = useState<TabKey>('data');
  const [invoices, setInvoices] = useState<InvoiceRow[] | null>(null);
  const [quotes, setQuotes] = useState<QuoteRow[] | null>(null);

  useEffect(() => {
    apiClient
      .get<Relation>(`/customers/${id}`)
      .then((r) => setRel(r.data))
      .catch(showApiError);
  }, [id, showApiError]);

  // Lazy-load the data feeds the first time their tab is opened.
  useEffect(() => {
    if ((tab === 'invoices' || tab === 'unpaid') && invoices === null) {
      apiClient
        .get<InvoiceRow[]>(`/invoices?customerId=${id}`)
        .then((r) => setInvoices(r.data ?? []))
        .catch(showApiError);
    }
    if (tab === 'offers' && quotes === null) {
      apiClient
        .get<QuoteRow[]>('/quotes')
        .then((r) => setQuotes((r.data ?? []).filter((q) => q.customerId === id)))
        .catch(showApiError);
    }
  }, [tab, id, invoices, quotes, showApiError]);

  // WP-2 — the credit ceiling is configured by ADM/OWN (SC-14 `PUT /customers/:id`).
  // `null` draft = not editing; a string draft = the inline editor is open.
  const role = useAuthStore((s) => s.user?.role);
  // 3.2 (2026-10-23): granting or changing a credit line is ADM/MGR/OWN.
  const canSetLimit = role === Role.ADMIN || role === Role.MANAGER || role === Role.OWNER;
  const [limitDraft, setLimitDraft] = useState<string | null>(null);
  const [savingLimit, setSavingLimit] = useState(false);
  // Styled confirmation in place of window.confirm — see components/ui/ConfirmDialog.
  const [confirm, confirmDialog] = useConfirm();

  const del = async () => {
    if (!rel) return;
    const ok = await confirm({
      title: t('relations.detail.deactivateTitle', { name: rel.name }),
      description: t('relations.detail.deactivateDescription'),
      confirmLabel: t('relations.detail.deactivate'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await apiClient.delete(`/customers/${id}`, {
        headers: { 'X-Command-Id': crypto.randomUUID() },
      });
      push({
        tone: 'success',
        message: t('relations.detail.toastDeactivated', { name: rel.name }),
      });
      navigate('/customers');
    } catch (err) {
      showApiError(err);
    }
  };

  const refresh = () =>
    apiClient
      .get<Relation>(`/customers/${id}`)
      .then((r) => setRel(r.data))
      .catch(showApiError);

  // §25 #4 — suspend blocks new orders and freezes in-flight ones; reactivate thaws them.
  const suspend = async () => {
    if (!rel) return;
    const ok = await confirm({
      title: t('relations.detail.suspendTitle', { name: rel.name }),
      description: t('relations.detail.suspendDescription'),
      confirmLabel: t('relations.detail.suspend'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const { data } = await apiClient.post<{ frozen: number }>(
        `/customers/${id}/suspend`,
        {},
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: 'success',
        message: t('relations.detail.toastSuspended', { name: rel.name, count: data.frozen }),
      });
      await refresh();
    } catch (err) {
      showApiError(err);
    }
  };

  /**
   * Bring a customer back to ACTIVE. ONE endpoint serves both ways out, because the server
   * sets `status='ACTIVE'` unconditionally and thaws whatever it finds frozen:
   *   • SUSPENDED (§25 #4 credit hold) → un-suspended, and the orders the hold froze resume
   *     the status they held before it;
   *   • INACTIVE (soft delete)         → restored to the book. A soft delete freezes nothing,
   *     so `thawed` is simply 0 and the message says so.
   * Without this the second case had no way back in the UI at all — a mis-clicked Deactivate
   * could only be undone with SQL.
   */
  const reactivate = async () => {
    if (!rel) return;
    const wasInactive = rel.status === 'INACTIVE';
    const ok = await confirm(
      wasInactive
        ? {
            title: t('relations.detail.reactivateTitle', { name: rel.name }),
            description: t('relations.detail.reactivateDescription'),
            confirmLabel: t('relations.detail.reactivate'),
          }
        : {
            title: t('relations.detail.unsuspendTitle', { name: rel.name }),
            description: t('relations.detail.unsuspendDescription'),
            confirmLabel: t('relations.detail.unsuspend'),
          }
    );
    if (!ok) return;
    try {
      const { data } = await apiClient.post<{ thawed: number }>(
        `/customers/${id}/reactivate`,
        {},
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: 'success',
        message: wasInactive
          ? t('relations.detail.toastReactivated', { name: rel.name })
          : data.thawed
            ? t('relations.detail.toastUnsuspendedResumed', { name: rel.name, count: data.thawed })
            : t('relations.detail.toastUnsuspended', { name: rel.name }),
      });
      await refresh();
    } catch (err) {
      showApiError(err);
    }
  };

  /**
   * WP-2 — set the ceiling the encours is checked against. `PUT /customers/:id` is the
   * SC-14 configure endpoint (ADM/OWN only), optimistic-locked on the version we read.
   * 0 is the "no ceiling" sentinel, not a zero allowance.
   */
  const saveLimit = async () => {
    if (!rel || limitDraft === null) return;
    // Migration 079 — three states: a blank field lifts the ceiling entirely (null), a number
    // is the ceiling, and 0 means no credit. Blank USED to be the way to say "unlimited" by
    // typing 0; that now means the opposite, so the empty field carries it instead.
    const blank = limitDraft.trim() === '';
    const value = blank ? null : Number(limitDraft);
    if (value !== null && (!Number.isFinite(value) || value < 0)) {
      push({
        tone: 'error',
        message: t('relations.detail.creditLimitInvalid'),
      });
      return;
    }
    setSavingLimit(true);
    try {
      await apiClient.put(
        `/customers/${id}`,
        { creditLimit: value, expectedVersion: rel.version },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: 'success',
        message:
          value === null
            ? t('relations.detail.creditLimitLifted')
            : t('relations.detail.creditLimitSet', { amount: formatEUR(value) }),
      });
      setLimitDraft(null);
      await refresh();
    } catch (err) {
      showApiError(err);
    } finally {
      setSavingLimit(false);
    }
  };

  if (!rel) return <Skeleton className="h-40 w-full" />;

  // WP-2 encours model: `reserved` is promised (ordered, not yet delivered), `balance` is
  // owed (delivered, not yet paid). A delivery MOVEs an amount from one to the other, so
  // their SUM — and therefore the available credit — does not change across a delivery.
  const num = (v: unknown) => Number(v ?? 0);
  // Migration 079: NULL = explicitly unlimited, 0 = no credit. `num()` cannot be used here —
  // it maps null to 0, which is now the OPPOSITE reading.
  const limit = rel.credit_limit == null ? null : Number(rel.credit_limit);
  const balance = num(rel.current_balance);
  const reserved = num(rel.reserved_credit);
  const encoursTotal = balance + reserved;
  const unlimited = limit === null;
  const available = limit === null ? null : Math.max(0, limit - encoursTotal);
  const overLimit = limit !== null && encoursTotal > limit;
  // A 0 ceiling is "fully used" the moment anything is owed, and 0/0 must not be NaN.
  const usedPct =
    limit === null
      ? 0
      : limit === 0
        ? encoursTotal > 0
          ? 100
          : 0
        : Math.min(100, Math.round((encoursTotal / limit) * 100));

  // A relation with no explicit status is live — treat null as ACTIVE rather than as a
  // fourth, unhandled state that would silently offer no lifecycle action at all.
  const status = rel.status ?? 'ACTIVE';

  const contact = (rel.contact ?? {}) as Record<string, unknown>;
  const bill = rel.billing_address ?? {};
  const ship = rel.shipping_address ?? {};
  const s = (v: unknown) => (v == null || v === '' ? null : String(v));

  return (
    <div className="space-y-5">
      {confirmDialog}
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={() => navigate('/customers')}
          className="inline-flex items-center gap-1.5 text-sm rounded-full bg-foreground text-background px-3 py-1.5"
        >
          <Search className="h-3.5 w-3.5" /> {t('relations.detail.searchRelation')}
        </button>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate(`/customers/${id}/edit`)}>
            <Pencil className="h-4 w-4" /> {t('relations.detail.edit')}
          </Button>
          {/*
            Three states, three offers — the two ways OUT of the book are distinct and so are
            the ways back, but they share one endpoint (see `reactivate`).
              ACTIVE    → Suspend (credit hold) · Deactivate (soft delete)
              SUSPENDED → Un-suspend · Deactivate
              INACTIVE  → Reactivate only; it is already deactivated, and suspending a
                          soft-deleted relation is meaningless.
          */}
          {status === 'ACTIVE' && (
            <Button size="sm" variant="outline" onClick={suspend}>
              <Ban className="h-4 w-4" /> {t('relations.detail.suspend')}
            </Button>
          )}
          {status === 'SUSPENDED' && (
            <Button size="sm" variant="outline" onClick={reactivate}>
              <RotateCcw className="h-4 w-4" /> {t('relations.detail.unsuspend')}
            </Button>
          )}
          {status === 'INACTIVE' && (
            <Button size="sm" variant="outline" onClick={reactivate}>
              <RotateCcw className="h-4 w-4" /> {t('relations.detail.reactivate')}
            </Button>
          )}
          {status !== 'INACTIVE' && (
            <Button size="sm" variant="outline" onClick={del}>
              <Trash2 className="h-4 w-4" /> {t('relations.detail.deactivate')}
            </Button>
          )}
        </div>
      </div>

      <h1 className="border-b pb-4 text-xl font-semibold tracking-tight">
        {t('relations.detail.title', { name: rel.name })}
        {rel.status === 'INACTIVE' && (
          <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-destructive/10 text-destructive align-middle">
            {t('relations.detail.inactive')}
          </span>
        )}
        {rel.status === 'SUSPENDED' && (
          <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-warning/10 text-warning align-middle">
            {t('relations.detail.suspended')}
          </span>
        )}
      </h1>

      {/*
        WP-2 — the encours. Two buckets against one ceiling:
          Reserved  = ordered, not yet delivered (a promise held at SALE_CREATE)
          Balance   = delivered, not yet paid    (a debt)
        ORDER_DELIVER MOVEs an amount from the first to the second in one transaction, so the
        SUM never moves — which is why "Available credit" is identical before and after a delivery.
      */}
      <section className="border rounded p-4 bg-card">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="font-semibold text-sm">{t('relations.detail.encoursTitle')}</h2>
          {canSetLimit &&
            (limitDraft === null ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setLimitDraft(limit === null ? '' : String(limit))}
              >
                <Pencil className="h-3.5 w-3.5" /> {t('relations.detail.setCreditLimit')}
              </Button>
            ) : (
              <div className="flex items-center gap-1.5">
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  autoFocus
                  className="w-32 h-8"
                  value={limitDraft}
                  placeholder={t('relations.detail.limitPlaceholder')}
                  onChange={(e) => setLimitDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void saveLimit();
                    if (e.key === 'Escape') setLimitDraft(null);
                  }}
                  aria-label={t('relations.detail.metricCreditLimit')}
                />
                <Button size="sm" onClick={() => void saveLimit()} disabled={savingLimit}>
                  <Check className="h-3.5 w-3.5" />{' '}
                  {savingLimit ? t('relations.detail.saving') : t('relations.detail.save')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setLimitDraft(null)}
                  aria-label={t('relations.detail.cancel')}
                  title={t('relations.detail.cancel')}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Metric
            label={t('relations.detail.metricCreditLimit')}
            value={unlimited ? t('relations.detail.noCeiling') : formatEUR(limit)}
            hint={t('relations.detail.metricCreditLimitHint')}
          />
          <Metric
            label={t('relations.detail.metricBalance')}
            value={formatEUR(balance)}
            hint={t('relations.detail.metricBalanceHint')}
          />
          <Metric
            label={t('relations.detail.metricReserved')}
            value={formatEUR(reserved)}
            hint={t('relations.detail.metricReservedHint')}
          />
          <Metric
            label={t('relations.detail.metricAvailable')}
            value={available === null ? '—' : formatEUR(available)}
            hint={
              overLimit
                ? t('relations.detail.metricOverLimit')
                : t('relations.detail.metricHeadroom')
            }
            tone={overLimit ? 'bad' : 'good'}
          />
        </div>

        {!unlimited && (
          <div
            className="mt-3 h-2 rounded bg-muted overflow-hidden"
            title={t('relations.detail.usedPct', { pct: usedPct })}
          >
            <div
              className={`h-full ${overLimit ? 'bg-destructive' : 'bg-primary'}`}
              style={{ width: `${usedPct}%` }}
            />
          </div>
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          {t('relations.detail.encoursExplainPrefix')} <strong>{formatEUR(encoursTotal)}</strong>
          {t('relations.detail.encoursExplainSuffix')}
        </p>
      </section>

      {/* Manual credit release — an adjustment ALONGSIDE the computed exposure above. */}
      <CustomerCreditReleases customerId={id!} onChanged={refresh} />

      {/* Money received on account, not yet applied (migration 139). */}
      <CustomerOnAccountMoney customerId={id!} onChanged={refresh} />

      {/* Account credit (migration 136): avoirs not yet used, and the manager-approved payout. */}
      <CustomerAccountCredit
        customerId={id!}
        canRequest={role === Role.MANAGER || role === Role.ADMIN || role === Role.OWNER}
      />

      {/* VAT exemption (migration 134): set by a manager / admin / owner, read by everyone. */}
      <CustomerVatExemption
        customerId={id!}
        customer={rel}
        canEdit={role === Role.MANAGER || role === Role.ADMIN || role === Role.OWNER}
        onChanged={refresh}
      />

      {/* Field display — mirrors the relation sheet; only stored fields carry values. */}
      <section className="border rounded p-4 bg-card">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-2 text-sm">
          <Item label={t('relations.fields.relationId')} value={rel.id.slice(0, 8)} mono />
          <Item label={t('relations.fields.name')} value={rel.name} />
          <Item label={t('relations.fields.email')} value={s(contact.email)} />
          <Item label={t('relations.fields.emailInvoice')} value={s(rel.email_invoice)} />
          <Item label={t('relations.fields.emailPackingSlip')} value={s(rel.email_packing_slip)} />
          <Item label={t('relations.fields.emailQuotation')} value={s(rel.email_quotation)} />
          <Item label={t('relations.fields.emailOrder')} value={s(rel.email_order)} />
          <Item label={t('relations.fields.phone')} value={s(contact.phone)} />
          <Item label={t('relations.fields.address')} value={s(bill.line1)} />
          <Item label={t('relations.fields.zipCode')} value={s(bill.postalCode)} />
          <Item label={t('relations.fields.place')} value={s(bill.city)} />
          <Item
            label={t('relations.fields.country')}
            value={
              bill.country
                ? t(`relations.countries.${bill.country}`, { defaultValue: bill.country })
                : null
            }
          />
          <Item label={t('relations.fields.deliveryAddress')} value={s(ship.line1)} />
          <Item label={t('relations.fields.siret')} value={s(rel.siret)} />
          <Item label={t('relations.fields.vatNumber')} value={s(rel.vat_number)} />
          <Item label={t('relations.fields.customerType')} value={rel.customer_type} />
          <Item label={t('relations.fields.relationGroup')} value={s(rel.buyer_group_id)} />
          {/* WP-2 — the terms are the regime; billing mode is their derived projection. */}
          <Item label={t('relations.fields.paymentTermsShort')} value={s(rel.payment_terms)} />
          <Item
            label={t('relations.fields.billingMode')}
            value={
              rel.billing_mode === 'DEFERRED'
                ? t('relations.billingModes.deferredMonthly')
                : t('relations.billingModes.immediate')
            }
          />
          <Item label={t('relations.fields.clientCode')} value={s(rel.client_code)} />
          <Item label={t('relations.fields.settlementChannel')} value={s(rel.settlement_channel)} />
          <Item
            label={t('relations.fields.billingType')}
            value={
              rel.billing_type
                ? t(`relations.billingTypes.${rel.billing_type}`, {
                    defaultValue: rel.billing_type,
                  })
                : null
            }
          />
          <Item
            label={t('relations.fields.creditPeriod')}
            value={
              rel.credit_period == null
                ? null
                : t('relations.detail.creditPeriodDays', { count: rel.credit_period })
            }
          />
          <Item
            label={t('relations.fields.creditLimit')}
            value={
              rel.credit_limit == null
                ? t('relations.detail.noCeiling')
                : formatEUR(rel.credit_limit)
            }
          />
          <Item label={t('relations.fields.iban')} value={s(rel.iban)} />
          <Item label={t('relations.fields.website')} value={s(rel.website)} />
          <Item label={t('relations.fields.agreement')} value={s(rel.agreement)} />
          <Item
            label={t('relations.fields.webshopDiscount')}
            value={
              rel.webshop_discount_percentage == null
                ? null
                : `${rel.webshop_discount_percentage} %`
            }
          />
          <BoolItem label={t('relations.fields.isCustomer')} on={rel.is_customer !== false} />
          <BoolItem label={t('relations.fields.isSupplier')} on={rel.is_supplier === true} />
          <BoolItem label={t('relations.fields.blockSale')} on={rel.block_sale === true} />
        </div>
      </section>

      {/* Sub-menu bar */}
      <div className="flex flex-wrap gap-2">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            className={`text-xs rounded-full border px-3 py-1.5 ${
              tab === tb.key
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card hover:bg-muted/40'
            }`}
          >
            {t(`relations.detail.tabs.${tb.key}`)}
          </button>
        ))}
      </div>

      <section className="border rounded p-4 bg-card min-h-[8rem]">
        {tab === 'data' && (
          <p className="text-sm text-muted-foreground">{t('relations.detail.dataTabHint')}</p>
        )}

        {(tab === 'invoices' || tab === 'unpaid') &&
          (invoices === null ? (
            <p className="text-sm text-muted-foreground">{t('relations.detail.loading')}</p>
          ) : (
            <InvoiceTable
              rows={
                tab === 'unpaid'
                  ? invoices.filter((i) => i.status !== 'PAID' && i.status !== 'CANCELLED')
                  : invoices
              }
            />
          ))}

        {tab === 'offers' &&
          (quotes === null ? (
            <p className="text-sm text-muted-foreground">{t('relations.detail.loading')}</p>
          ) : quotes.length === 0 ? (
            <Empty />
          ) : (
            <table className="w-full text-sm">
              <thead className="text-muted-foreground border-b">
                <tr>
                  <th className="text-left py-1">{t('relations.detail.colNumber')}</th>
                  <th className="text-left">{t('relations.detail.colStatus')}</th>
                  <th className="text-right">{t('relations.detail.colTotalTtc')}</th>
                  <th className="text-right">{t('relations.detail.colValidUntil')}</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => (
                  <tr
                    key={q.id}
                    className="border-b last:border-0 hover:bg-muted/30 cursor-pointer"
                    onClick={() => navigate(`/quotes/${q.id}`)}
                  >
                    <td className="py-1">{q.quoteNumber ?? q.id.slice(0, 8)}</td>
                    <td>{t(`domain.quoteStatus.${q.status}`, { defaultValue: q.status })}</td>
                    <td className="text-right">{formatEUR(q.totalTTC)}</td>
                    <td className="text-right">{formatDate(q.expiresAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}

        {tab === 'documents' && <CustomerDocumentOverrides customerId={id} canEdit={true} />}

        {tab === 'users' && <CustomerB2bUsers customerId={id} />}

        {tab === 'collectors' && <CustomerCollectors customerId={id} />}

        {!['data', 'invoices', 'unpaid', 'offers', 'documents', 'users', 'collectors'].includes(
          tab
        ) && (
          <p className="text-sm text-muted-foreground">
            {t('relations.detail.comingSoon', { tab: t(`relations.detail.tabs.${tab}`) })}
          </p>
        )}
      </section>
    </div>
  );
}

function InvoiceTable({ rows }: { rows: InvoiceRow[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (rows.length === 0) return <Empty />;
  return (
    <table className="w-full text-sm">
      <thead className="text-muted-foreground border-b">
        <tr>
          <th className="text-left py-1">{t('relations.detail.colNumber')}</th>
          <th className="text-left">{t('relations.detail.colStatus')}</th>
          <th className="text-right">{t('relations.detail.colTotalTtc')}</th>
          <th className="text-right">{t('relations.detail.colIssuedOn')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((i) => (
          <tr
            key={i.id}
            className="border-b last:border-0 hover:bg-muted/30 cursor-pointer"
            onClick={() => navigate(`/invoices`)}
          >
            <td className="py-1">{i.invoice_number}</td>
            <td>{t(`domain.invoiceStatus.${i.status}`, { defaultValue: i.status })}</td>
            <td className="text-right">{formatEUR(i.total_ttc)}</td>
            <td className="text-right">{formatDate(i.issued_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Empty() {
  const { t } = useTranslation();
  return <p className="text-sm text-muted-foreground">{t('relations.detail.empty')}</p>;
}

/** One figure of the encours panel: big number, quiet label, optional tone. */
function Metric({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'good' | 'bad';
}) {
  const toneClass = tone === 'bad' ? 'text-destructive' : tone === 'good' ? 'text-success' : '';
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${toneClass}`}>{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground/70">{hint}</div>}
    </div>
  );
}

function Item({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground shrink-0">{label} :</span>
      <span
        className={`min-w-0 truncate max-w-[18rem] ${mono ? 'font-mono text-xs' : ''}`}
        title={value ?? undefined}
      >
        {value ?? <span className="text-muted-foreground/50">—</span>}
      </span>
    </div>
  );
}

function BoolItem({ label, on }: { label: string; on: boolean }): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground">{label} :</span>
      <span
        className={`text-xs px-1.5 py-0.5 rounded ${on ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'}`}
      >
        {on ? t('relations.detail.yes') : t('relations.detail.no')}
      </span>
    </div>
  );
}
